import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/tr';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { useSelector } from 'react-redux';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

moment.locale('tr');

export default function MallManagementHomeDelivery() {
  const theme = useTheme();
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = customer?.customer?.id;
  const token = parsedUser?.token;

  const [analyticsData, setAnalyticsData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  
  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  // Check service permission using hook
  const { hasPermission: homeDeliveryPermission, loading: permissionLoading } = useServicePermission(SERVICES.HOME_DELIVERY);

  const [startDate, setStartDate] = useState(moment().subtract(7, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [regionMonthly, setRegionMonthly] = useState('Mahalle');
  const [regionWeekly, setRegionWeekly] = useState('Mahalle');
  const [heatmapDataType, setHeatmapDataType] = useState('Mahalle');
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);
  
  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Eve Teslim',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  const fetchAnalyticsData = useCallback(async () => {
    if (permissionLoading) {
      return;
    }
    setLoadingAnalytics(true);
    try {
      if (!token || !mallID) {
        setLoadingAnalytics(false);
        return;
      }
      const userHasPermission = homeDeliveryPermission;
      setHasPermission(userHasPermission);

      if (userHasPermission) {
        const response = await Services.GetLiveAnalyticsHomeDelivery(token, mallID);
        if (response.data.status && response.data.result) {
          setAnalyticsData(response.data.result);
          setTableData(Array.isArray(response.data.result.homeDeliveryList) ? response.data.result.homeDeliveryList : []);
        } else {
          toast.error(response.data.message || 'Veriler alınamadı.');
          setAnalyticsData({});
          setTableData([]);
        }
      } else {
        setAnalyticsData({});
        setTableData([]);
        setShowApplyBanner(true);
      }
    } catch (error) {
      toast.error('Analitik verileri alınırken bir hata oluştu.');
      setAnalyticsData({});
      setTableData([]);
    } finally {
      setLoadingAnalytics(false);
    }
  }, [token, mallID, homeDeliveryPermission, permissionLoading]);

  const fetchFilteredList = useCallback(async () => {
    if (!hasPermission) return;
    setLoadingTable(true);
    try {
      if (!token || !mallID) return;
      const response = await Services.GetLiveAnalyticsHomeDeliveryList(token, mallID, startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'));
      if (response.data.status && response.data.result) {
        setTableData(Array.isArray(response.data.result) ? response.data.result : []);
      } else {
        toast.error(response.data.message || 'Liste verisi alınamadı.');
      }
    } catch (error) {
      toast.error('Liste verileri alınırken bir hata oluştu.');
    } finally {
      setLoadingTable(false);
    }
  }, [token, mallID, startDate, endDate, hasPermission]);
  
  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') { return series.map(() => 0); }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] }));
    }
    return [];
  };

  const processedData = useMemo(() => {
    if (!analyticsData) return { totalDailyPostUsage: 0, monthlyDonut: { labels: [], series: [] }, weeklyDonut: { labels: [], series: [] }, monthlyCombo: { categories: [], usageData: [], revenueData: [] }, weeklyCombo: { categories: [], usageData: [], revenueData: [] } };

    const { totalDailyPostUsage, totalCurrentMonthCityTownNeighbourUsage, totalCurrentWeekCityTownNeighbourUsage, monthlyUsage, weeklyUsage } = analyticsData;

    const processLocationData = (data, level) => {
      const aggregated = {};
      if (data && data.length > 0) {
        data.forEach(cityObj => Object.values(cityObj).forEach(districts => Object.entries(districts).forEach(([districtName, neighbourhoods]) => {
          if (level === 'İlçe') {
            aggregated[districtName] = (aggregated[districtName] || 0) + Object.values(neighbourhoods).reduce((a, b) => a + b, 0);
          } else {
            Object.entries(neighbourhoods).forEach(([neighbourhoodName, count]) => {
              aggregated[neighbourhoodName] = (aggregated[neighbourhoodName] || 0) + count;
            });
          }
        })));
      }
      const total = Object.values(aggregated).reduce((a, b) => a + b, 0);
      return { labels: Object.keys(aggregated), series: total > 0 ? Object.values(aggregated).map(v => parseFloat(((v / total) * 100).toFixed(1))) : [] };
    };

    const monthlyDonut = processLocationData(totalCurrentMonthCityTownNeighbourUsage, regionMonthly);
    const weeklyDonut = processLocationData(totalCurrentWeekCityTownNeighbourUsage, regionWeekly);

    const processUsageData = (data, isWeekly) => {
      const result = { categories: [], usageData: [], revenueData: [] };
      if (data) {
        const sortedKeys = isWeekly ? Object.keys(data).sort((a, b) => moment(a, "YYYY-MM-DD").diff(moment(b, "YYYY-MM-DD"))) : ['1-7', '8-14', '15-21', `22-${moment().daysInMonth()}`];
        sortedKeys.forEach(key => {
          const item = data[key] || {};
          let totalUsage = isWeekly ? (item.usageCount || 0) : Object.values(item).reduce((sum, hour) => sum + (hour.usageCount || 0), 0);
          let totalPrice = isWeekly ? (item.totalPrice || 0) : Object.values(item).reduce((sum, hour) => sum + (hour.totalPrice || 0), 0);
          result.categories.push(isWeekly ? moment(key).format('DD MMM') : `Hafta (${key})`);
          result.usageData.push(totalUsage);
          result.revenueData.push(totalPrice);
        });
      }
      return result;
    };

    const monthlyCombo = processUsageData(monthlyUsage, false);
    const weeklyCombo = processUsageData(weeklyUsage, true);

    return { totalDailyPostUsage, monthlyDonut, weeklyDonut, monthlyCombo, weeklyCombo };
  }, [analyticsData, regionMonthly, regionWeekly]);

  const dailyHeatmapChart = useMemo(() => {
    const rawData = analyticsData?.dayHourlyUsageTotalCount;
    const dataTypeKey = heatmapDataType === 'İlçe' ? 'ilce' : 'mahalle';
    const data = rawData?.[dataTypeKey] || {};
    if (!rawData || Object.keys(data).length === 0) return { series: [], options: {}, maxCount: 10, colors: [] };
    const allTimeSlots = Array.from({ length: 16 }, (_, i) => { const h = 8 + i, n = h + 1; return `${h.toString().padStart(2, '0')}:00-${n === 24 ? '00' : n.toString().padStart(2, '0')}:00`; });
    const daysOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const turkishDays = { "Monday": "P.tesi", "Tuesday": "Salı", "Wednesday": "Çarş.", "Thursday": "Perş.", "Friday": "Cuma", "Saturday": "C.tesi", "Sunday": "Pazar" };

    // Her saat için satır toplamlarını hesapla
    const hourlyTotals = allTimeSlots.map(timeSlot => {
      return daysOrder.reduce((sum, dayKey) => sum + (data[dayKey.toLowerCase()]?.[timeSlot] || 0), 0);
    });

    // Her gün için sütun toplamlarını hesapla
    const dailyTotals = daysOrder.map(dayKey => {
      return allTimeSlots.reduce((sum, timeSlot) => sum + (data[dayKey.toLowerCase()]?.[timeSlot] || 0), 0);
    });

    // Genel toplam
    const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

    // En alta günlük toplamlar satırı ekle
    const totalRow = {
      name: 'Toplam',
      data: [
        ...daysOrder.map((dayKey, index) => ({ x: turkishDays[dayKey], y: dailyTotals[index] })),
        { x: 'Toplam', y: grandTotal }
      ]
    };

    // Series oluştur - her satıra "Toplam" sütunu ekle
    const series = [
      totalRow,
      ...allTimeSlots.map((timeSlot, index) => ({
        name: timeSlot.split('-')[0],
        data: [
          ...daysOrder.map(dayKey => ({ x: turkishDays[dayKey], y: data[dayKey.toLowerCase()]?.[timeSlot] || 0 })),
          { x: 'Toplam', y: hourlyTotals[index] }
        ]
      })).reverse()
    ];

    const allCounts = Object.values(data).flatMap(day => (day && typeof day === 'object') ? Object.values(day) : []);
    const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
    const ilceColors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
    const mahalleColors = ['#D7D7D7', '#89D6CE', '#54C4B8', '#14B8A6', '#188B7E'];
    const activeColors = heatmapDataType === 'İlçe' ? ilceColors : mahalleColors;
    const options = {
      chart: { type: 'heatmap', toolbar: { show: false } },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.5,
          radius: 2,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: activeColors[0] },
              { from: 1, to: Math.floor(maxCount * 0.25), color: activeColors[2] },
              { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: activeColors[3] },
              { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: activeColors[4] }
            ]
          }
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ['#000'],
          fontSize: '12px',
          fontWeight: 500
        }
      },
      stroke: { show: true, width: 3, colors: ['#fff'] },
      xaxis: { type: 'category' },
      yaxis: { labels: { style: { fontSize: '11px' } } },
      legend: { show: false },
      tooltip: { y: { formatter: (value) => `${value} adet` } }
    };
    return { series, options, maxCount, colors: activeColors };
  }, [analyticsData, heatmapDataType]);

  const paginatedRows = useMemo(() => tableData.slice((page - 1) * rowsPerPage, page * rowsPerPage), [tableData, page, rowsPerPage]);
  const handleClearFilters = () => { if (!hasPermission) return; setStartDate(moment().subtract(7, 'days')); setEndDate(moment()); setTableData(analyticsData?.homeDeliveryList || []); };
  
  const maskName = (name) => { if (!name) return '*******'; const parts = name.split(' '); return parts.map(p => p.length > 1 ? p.charAt(0) + '*'.repeat(p.length - 1) : p).join(' '); };
  const maskPhone = (phone) => { if (!phone || phone.length < 10) return '**********'; return `${phone.slice(0, 3)}****${phone.slice(-2)}`; };
  const maskAddress = (address) => { if (!address) return '*******'; return address.split(' ').map(w => w.length <= 2 ? w : w.slice(0, 2) + '*'.repeat(w.length - 2)).join(' '); };

  const donutOptions = (labels) => ({ chart: { type: 'donut', toolbar: { show: false } }, labels, colors: ['#1C64F2', '#E74694', '#FDBA8C', '#14B8A6'], plotOptions: { pie: { donut: { size: '65%', labels: { show: true, name: { show: true }, value: { show: true, formatter: (val) => `% ${val}` }, total: { show: false } } } } }, dataLabels: { enabled: false }, legend: { position: 'bottom' } });
    const comboOptions = (categories) => ({ 
    chart: { stacked: false, toolbar: { show: false } }, 
    stroke: { width: [0, 2] }, 
    colors: ['#14B8A6', '#1C64F2',], 
    xaxis: { categories }, 
    yaxis: [ 
      {         
        title: { text: 'Kullanım Adedi' },
        labels: {
          formatter: (val) => val.toFixed(0)
        }
      }, 
      {         
        opposite: true, 
        title: { text: 'Ciro' }, 
        labels: {
          formatter: (val) => `${val.toFixed(0)} ₺` 
        }
      }
    ], 
    legend: { position: 'bottom' },    
  });
  

  if (loadingAnalytics || permissionLoading) { return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>; }

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Helmet><title>Eve Teslimat</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
              <Stack direction="row" gap={1} alignItems="center">
                <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{padding: '10px 16px', cursor: 'pointer'}}>Başvur</Button>
                <IconButton aria-label="close" color="inherit" size="small" sx={{cursor: 'pointer'}} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
              </Stack>
            } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Eve Teslimat</Typography>
        <Button variant="text" startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} onClick={handleOpenVideoModal}>Tanıtım Videosunu İzle</Button>
      </Stack>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, minHeight: 140, flex: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Günlük Kullanım Adedi (Bugün)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Toplam: <strong>{processedData.totalDailyPostUsage ?? 0}</strong></Typography>
              <Typography variant="body2" color="text.secondary">Ortalama: <strong>-</strong></Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, background: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Iconify icon="eva:layers-outline" width={20} height={20} sx={{ color: '#fff' }} /></Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}><Box><Typography variant="h6">Aylık Eve Teslim Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Teslimat Yerine Göre</Typography></Box><FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Bölge</InputLabel><Select value={regionMonthly} label="Bölge" onChange={(e) => setRegionMonthly(e.target.value)}><MenuItem value="Mahalle">Mahalle</MenuItem><MenuItem value="İlçe">İlçe</MenuItem></Select></FormControl></Box>
            <ReactApexChart options={donutOptions(processedData.monthlyDonut.labels)} series={hasPermission ? processedData.monthlyDonut.series : zeroOutSeries(processedData.monthlyDonut.series)} type="donut" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Box><Typography variant="h6" gutterBottom>Aylık Kullanım ve Ciro Hareketleri</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Toplam Kullanım Adedi - Toplam Ciro</Typography></Box>
            <ReactApexChart options={comboOptions(processedData.monthlyCombo.categories)} series={hasPermission ? [{ name: 'Kullanım Adedi', type: 'line', data: processedData.monthlyCombo.usageData }, { name: 'Ciro', type: 'column', data: processedData.monthlyCombo.revenueData }] : zeroOutSeries([{ name: 'Kullanım Adedi', type: 'column', data: processedData.monthlyCombo.usageData }, { name: 'Ciro', type: 'line', data: processedData.monthlyCombo.revenueData }])} type="line" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}><Box><Typography variant="h6">Haftalık Eve Teslim Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Teslimat Yerine Göre</Typography></Box><FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Bölge</InputLabel><Select value={regionWeekly} label="Bölge" onChange={(e) => setRegionWeekly(e.target.value)}><MenuItem value="Mahalle">Mahalle</MenuItem><MenuItem value="İlçe">İlçe</MenuItem></Select></FormControl></Box>
            <ReactApexChart options={donutOptions(processedData.weeklyDonut.labels)} series={hasPermission ? processedData.weeklyDonut.series : zeroOutSeries(processedData.weeklyDonut.series)} type="donut" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Haftalık Kullanım ve Ciro Hareketleri</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={comboOptions(processedData.weeklyCombo.categories)} series={hasPermission ? [{ name: 'Kullanım Adedi', type: 'line', data: processedData.weeklyCombo.usageData }, { name: 'Ciro', type: 'column', data: processedData.weeklyCombo.revenueData }] : zeroOutSeries([{ name: 'Kullanım Adedi', type: 'column', data: processedData.weeklyCombo.usageData }, { name: 'Ciro', type: 'line', data: processedData.weeklyCombo.revenueData }])} type="line" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 2, mt: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Box><Typography variant="h6">Gün ve Saat Bazlı Eve Teslim Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Kullanım Yoğunluğu</Typography></Box>
              <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Veri Tipi</InputLabel><Select value={heatmapDataType} label="Veri Tipi" onChange={(e) => setHeatmapDataType(e.target.value)}><MenuItem value="Mahalle">Mahalle</MenuItem><MenuItem value="İlçe">İlçe</MenuItem></Select></FormControl>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}><ReactApexChart options={dailyHeatmapChart.options} series={hasPermission ? dailyHeatmapChart.series : zeroOutSeries(dailyHeatmapChart.series)} type="heatmap" height={350} /></Box>
              <HeatmapLegend maxCount={dailyHeatmapChart.maxCount} colors={dailyHeatmapChart.colors} label="Kullanım Adedi" />
            </Stack>
          </Card>
        </Grid>
      </Grid>
      
      <Paper sx={{ mt: 4, p: 3, bgcolor: 'background.paper', position: 'relative' }} elevation={0}>
        {loadingTable && <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 2 }}><CircularProgress /></Box>}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Başlangıç Tarihi" value={startDate} onChange={setStartDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Bitiş Tarihi" value={endDate} onChange={setEndDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} md={2}><Button fullWidth variant="outlined" size="medium" onClick={handleClearFilters} disabled={!hasPermission}>TEMİZLE</Button></Grid>
          <Grid item xs={6} md={2}><Button fullWidth variant="contained" size="medium" onClick={fetchFilteredList} disabled={!hasPermission}>LİSTELE</Button></Grid>
        </Grid>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.paper' }}><TableRow>{['NO', 'AD SOYAD', 'TELEFON', 'ADRES', 'TARİH', 'DURUM'].map((h) => (<TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>))}</TableRow></TableHead>
            <TableBody>
              {paginatedRows.map((r, index) => (<TableRow key={r.id || index} hover><TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell><TableCell>{maskName(r.nameSurName)}</TableCell><TableCell>{maskPhone(r.gsmNumber)}</TableCell><TableCell>{maskAddress(r.locationCode)}</TableCell><TableCell>{moment(r.startDate).format('DD.MM.YYYY HH:mm')}</TableCell><TableCell>{r.status ? 'Aktif' : 'Pasif'}</TableCell></TableRow>))}
            </TableBody>
            <TableFooter>
              <TableRow><TableCell colSpan={6} sx={{ py: 2 }}><Box display="flex" alignItems="center" justifyContent="space-between"><Typography variant="caption">Toplam {tableData.length} kayıt</Typography><Pagination count={Math.ceil(tableData.length / rowsPerPage)} page={page} onChange={(e, newPage) => setPage(newPage)} shape="rounded" size="small" /></Box></TableCell></TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
      
      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Videosu<IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500], }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers><video width="100%" height="auto" controls autoPlay src="https://files.easypoint.com.tr/files/data-ekranlari/home-delivery.mp4">Tarayıcınız video etiketini desteklemiyor.</video></DialogContent>
      </Dialog>
      
      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}